import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Display, Label } from '@growtherapy/sprout-ui';

import flowerShadowBackground from '../../assets/backgrounds/flower-shadow.jpg';
import { Navbar, PageContainer, PageProviders } from '../../components';
import { logger } from '../../datadog';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { Theme } from '../../state';
import { useEnterWaitingRoomCallback } from '../nav/useEnterWaitingRoomCallback';
import { didManuallyResolveSoftBlockAtom, isSoftBlockResolvedAtom, visitorNameAtom } from '../state';
import { PreSessionMeasures } from './PreSessionMeasures';
import { SoftBlockAutoTransitionToast } from './SoftBlockAutoTransitionToast';
import { SoftBlockJoiningTransition } from './SoftBlockJoiningTransition';
import { useSoftBlockAutoTransition } from './useSoftBlockAutoTransition';
import { useSoftBlockFlowTracking, useTrackSoftBlockGetStartedView } from './useSoftBlockTracking';

export function Page({ providerName, onPrimaryAction }: { providerName: string; onPrimaryAction: () => void }) {
  const t = useTranslate();
  useTrackSoftBlockGetStartedView();

  const title = t('telehealth-core:preSessionMeasures.helpProviderUnderstandHowYoureDoing', { providerName });
  const body = t('telehealth-core:preSessionMeasures.completeABriefCheckIn');
  const ctaLabel = t('telehealth-core:preSessionMeasures.getStarted');

  return (
    <PageProviders theme={Theme.LIGHT}>
      {/* Side-by-side from `md` up; narrower screens stack the image below. */}
      <PageContainer className='md:flex-row'>
        <div className='flex w-full flex-col md:w-1/2'>
          <Navbar showLanguageSwitcher />

          <div className='flex flex-1 flex-col justify-end px-6 pb-12 md:pb-24 lg:px-12 lg:pb-24 motion-safe:animate-[appear_2s_cubic-bezier(0.16,1,0.3,1)]'>
            <div className='mb-component-150 gap-component-75 flex flex-col'>
              <Label.Small className='text-neutral-700 rebrand:text-text-subtle'>
                {t('telehealth-core:preSessionMeasures.beforeYourSession')}
              </Label.Small>

              {/* Smaller heading only while side-by-side in the narrow half (md–lg);
                  full size when stacked (full width) or when there's room at `lg`. */}
              <Display.Small className='text-neutral-800 rebrand:text-text-default hidden md:block lg:hidden'>
                {title}
              </Display.Small>
              <Display.Default className='text-neutral-800 rebrand:text-text-default block md:hidden lg:block'>
                {title}
              </Display.Default>

              <Body.Default className='text-neutral-700 rebrand:text-text-subtle'>{body}</Body.Default>
            </div>

            <Button onClick={onPrimaryAction} use='primary' className='self-start'>
              {ctaLabel}
            </Button>
          </div>
        </div>

        {/* Decorative image: right half from `md` up, bottom on narrower screens.
            flex-1 + min-h-0 + object-cover lets it crop to leftover space without
            ever forcing the page to scroll. */}
        <div className='min-h-0 flex-1 md:w-1/2 md:flex-none'>
          <img src={flowerShadowBackground} alt='' className='h-full w-full object-cover' />
        </div>
      </PageContainer>
    </PageProviders>
  );
}

export function PageWrapper() {
  const [isFillingMeasures, setIsFillingMeasures] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const setIsSoftBlockResolved = useSetAtom(isSoftBlockResolvedAtom);
  const setDidManuallyResolveSoftBlock = useSetAtom(didManuallyResolveSoftBlockAtom);
  const visitorName = useAtomValue(visitorNameAtom);
  const { trackGetStartedClicked, trackGuardrailPromptShown, trackAutoTransitioned } = useSoftBlockFlowTracking();

  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const providerName = data?.telehealthSessionInfo.provider.name ?? 'your provider';

  const goToWaitingRoom = useCallback(() => {
    // Resolving the soft block transitions to the waiting room (via
    // `enterWaitingRoom`) and prevents re-entering the soft block. Manual exits
    // (skip / completion) also flag `didManuallyResolveSoftBlock` so the
    // waiting-room measures drawer auto-open stays suppressed — the client was
    // already given the chance to complete measures here.
    setIsSoftBlockResolved(true);
    setDidManuallyResolveSoftBlock(true);
    enterWaitingRoom(visitorName);
  }, [enterWaitingRoom, setIsSoftBlockResolved, setDidManuallyResolveSoftBlock, visitorName]);

  // Completing the final measure (Submit & join) shows a brief "Joining session
  // now..." interstitial before entering the waiting room. The soft block is
  // already resolved on submit (see `PreSessionMeasuresActions`), so this is
  // purely a transitional loading screen.
  const handleMeasuresComplete = useCallback(() => {
    logger.info('Soft-block resolved', {
      feature: 'soft_block',
      outcome: 'completed',
      patientShortId,
      providerShortId,
    });
    setIsJoining(true);
  }, [patientShortId, providerShortId]);

  const handleSkip = useCallback(() => {
    logger.info('Soft-block resolved', {
      feature: 'soft_block',
      outcome: 'skipped',
      patientShortId,
      providerShortId,
    });
    goToWaitingRoom();
  }, [goToWaitingRoom, patientShortId, providerShortId]);

  const autoTransitionToWaitingRoom = useCallback(() => {
    // Auto-transition (ran out of time) resolves the soft block so the client
    // exits to the waiting room, but intentionally does NOT flag manual
    // resolution: they never got to act on their measures, so the waiting-room
    // drawer should still auto-open to prompt them.
    logger.info('Soft-block resolved', {
      feature: 'soft_block',
      outcome: 'auto_transitioned',
      patientShortId,
      providerShortId,
    });
    trackAutoTransitioned();
    setIsSoftBlockResolved(true);
    enterWaitingRoom(visitorName);
  }, [trackAutoTransitioned, enterWaitingRoom, setIsSoftBlockResolved, visitorName, patientShortId, providerShortId]);

  // Guardrail: warn then auto-transition the client to the waiting room if the
  // soft-block flow starts cutting into their appointment start time. Applies
  // across every page of the flow.
  const { secondsToAutoTransition, autoTransitionToastIsVisible } = useSoftBlockAutoTransition({
    data,
    onAutoTransition: autoTransitionToWaitingRoom,
    onPromptShown: trackGuardrailPromptShown,
  });

  const handlePrimaryAction = useCallback(() => {
    trackGetStartedClicked();
    setIsFillingMeasures(true);
  }, [trackGetStartedClicked]);

  return (
    <>
      {autoTransitionToastIsVisible && (
        <SoftBlockAutoTransitionToast secondsToAutoTransition={secondsToAutoTransition} />
      )}
      {isJoining ? (
        <SoftBlockJoiningTransition onTransitionComplete={goToWaitingRoom} />
      ) : isFillingMeasures ? (
        <PreSessionMeasures onComplete={handleMeasuresComplete} onSkip={handleSkip} />
      ) : (
        <Page providerName={providerName} onPrimaryAction={handlePrimaryAction} />
      )}
    </>
  );
}
